/* The homepage's seasonal window: a film of the Signature box being packed by
   candlelight (made in studio/hero-film), with the name typed into the hero
   written onto its card. season.py writes the markup and #season-data; this
   plays it.

   The film is two files: the intro (the things go in, the card is set down)
   plays once, then the loop (the candles breathing, the camera drifting) plays
   on repeat underneath it from the moment the intro ends. The name is not in
   the film. film.json says where the card's name panel is on every frame from
   the moment it is written, and the page lays the typed name onto exactly that
   quadrilateral (a CSS homography), in ink that multiplies with the paper so
   the candlelight falls on it too.

   Every state is a finished picture: reduced motion, a Back visit, a film that
   has not started within six seconds, a browser that will not autoplay or play
   either codec - each shows the finished frame, and the name is still written
   on it. */
(function () {
  'use strict';
  var fig = document.getElementById('season');
  var stage = fig && fig.querySelector('.season-stage');
  var dataEl = document.getElementById('season-data');
  if (!stage || !dataEl) return;
  var D;
  try { D = JSON.parse(dataEl.textContent); } catch (e) { return; }

  var FPS = D.fps, W0 = D.write[0], WN = D.write[1], PW = D.name_px[0], PH = D.name_px[1];
  var SIZE = 190;                                   /* the name's type size in the panel */
  var DEFAULT = D.card.name;
  var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  var still = reduced || (nav && nav.type === 'back_forward') || fig.classList.contains('is-late');
  var rvfc = 'requestVideoFrameCallback' in HTMLVideoElement.prototype;

  /* ------------------------------------------------------------ posters */
  function put(k) {
    var t = document.getElementById('season-posters');
    var cur = stage.querySelector('.season-poster');
    if (!t || (cur && cur.getAttribute('data-poster') === k)) return;
    var img = t.content.querySelector('[data-poster="' + k + '"]').cloneNode(true);
    if (cur) cur.replaceWith(img); else stage.prepend(img);
  }

  /* ------------------------------------------------------------ the name */
  /* Drawn into a canvas about as many pixels as it covers on screen, then
     laid onto the panel's four corners: the browser warps a bitmap near 1:1,
     so the ink keeps its antialiasing (text itself under a 3D transform is
     rasterised once and scaled, and comes out jagged). The canvas is laid out
     at its backing size, one CSS pixel per canvas pixel, and the homography
     shrinks it onto the panel: laid out at its on-screen CSS size, Chromium
     drew it under the perspective at one pixel per CSS pixel whatever its
     backing store, and on a 3x phone the name came out blocky (30 Sep;
     hero-check measures the ink's edges). */
  var panel = document.createElement('canvas');
  panel.className = 'season-name';
  panel.setAttribute('aria-hidden', 'true');
  var g = panel.getContext('2d');
  stage.appendChild(panel);
  var inkColour = getComputedStyle(fig).getPropertyValue('--season-card-ink').trim() || '#2b3a5a';

  var name = DEFAULT, frame = 0, drawn = '', painted = '', cssW = 0, cssH = 0, SRC = null, fitted = null;

  /* the projective map from the canvas's own box onto four points */
  function adj(m) {
    return [m[4] * m[8] - m[5] * m[7], m[2] * m[7] - m[1] * m[8], m[1] * m[5] - m[2] * m[4],
      m[5] * m[6] - m[3] * m[8], m[0] * m[8] - m[2] * m[6], m[2] * m[3] - m[0] * m[5],
      m[3] * m[7] - m[4] * m[6], m[1] * m[6] - m[0] * m[7], m[0] * m[4] - m[1] * m[3]];
  }
  function mul(a, b) {
    var c = [];
    for (var i = 0; i < 3; i++) for (var j = 0; j < 3; j++) c[3 * i + j] = a[3 * i] * b[j] + a[3 * i + 1] * b[3 + j] + a[3 * i + 2] * b[6 + j];
    return c;
  }
  function basis(p) {
    var m = [p[0], p[2], p[4], p[1], p[3], p[5], 1, 1, 1], a = adj(m);
    var v = [a[0] * p[6] + a[1] * p[7] + a[2], a[3] * p[6] + a[4] * p[7] + a[5], a[6] * p[6] + a[7] * p[7] + a[8]];
    return mul(m, [v[0], 0, 0, 0, v[1], 0, 0, 0, v[2]]);
  }
  function homography(q) {
    var t = mul(basis(q), SRC);
    for (var i = 0; i < 9; i++) t[i] /= t[8];
    return 'matrix3d(' + [t[0], t[3], 0, t[6], t[1], t[4], 0, t[7], 0, 0, 1, 0, t[2], t[5], 0, 1].join(',') + ')';
  }

  /* size the canvas to the panel's size on screen; only when it changes by
     more than a few per cent, so the camera's last drift does not resize it */
  function size(q) {
    var w = Math.max(Math.hypot(q[2] - q[0], q[3] - q[1]), Math.hypot(q[4] - q[6], q[5] - q[7]));
    if (cssW && Math.abs(w / cssW - 1) < 0.06) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    cssW = Math.max(1, Math.round(w)); cssH = Math.max(1, Math.round(w * PH / PW));
    var W = panel.width = Math.round(cssW * dpr), H = panel.height = Math.round(cssH * dpr);
    panel.style.width = W + 'px'; panel.style.height = H + 'px';
    SRC = adj(basis([0, 0, W, 0, W, H, 0, H]));
    painted = '';
  }

  /* the name, fitted: long names shrink, they never wrap or clip; u of it
     written, behind a soft nib sweeping left to right */
  function paint(u) {
    var key = name + '|' + panel.width + '|' + u.toFixed(3);
    if (key === painted) return;
    painted = key;
    var W = panel.width, H = panel.height, px = SIZE * W / PW;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalCompositeOperation = 'source-over';
    g.clearRect(0, 0, W, H);
    g.font = 'italic 400 ' + px + 'px Fraunces, Georgia, serif';
    var tw = g.measureText(name).width;
    if (tw > W) { px *= W / tw; g.font = 'italic 400 ' + px + 'px Fraunces, Georgia, serif'; tw = W; }
    fitted = { width: tw / W * PW, size: px * PW / W };
    g.fillStyle = inkColour;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(name, W / 2, H / 2);
    if (u < 1) {
      var soft = tw * 0.12, x = (W - tw) / 2 + (tw + soft) * u;
      var nib = g.createLinearGradient(x - soft, 0, x, 0);
      nib.addColorStop(0, '#000');
      nib.addColorStop(1, 'rgba(0,0,0,0)');
      g.globalCompositeOperation = 'destination-in';
      g.fillStyle = nib;
      g.fillRect(0, 0, W, H);
    }
  }

  /* where the panel is on film frame f (1-based), and how much of the name is written */
  function draw(f) {
    frame = f;
    if (f < W0 || (still && name === DEFAULT)) { if (drawn !== 'off') { panel.classList.remove('is-on'); drawn = 'off'; } return; }
    var i = Math.min(f - W0, D.panel.length - 1), c = D.panel[i];
    var sw = stage.clientWidth, sh = stage.clientHeight;
    var u = Math.min(1, (f - W0) / WN);
    u = u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2;
    var q = [c[0] * sw, c[1] * sh, c[2] * sw, c[3] * sh, c[4] * sw, c[5] * sh, c[6] * sw, c[7] * sh];
    size(q);
    paint(u);
    var key = i + ',' + sw + ',' + sh + ',' + cssW;
    if (key !== drawn) { drawn = key; panel.style.transform = homography(q); }
    panel.classList.add('is-on');
  }
  var END = Infinity;

  /* the still picture: the finished frame. With the season's own default name
     it is the build's poster, name and all; any other name is laid on. */
  function showStill() {
    still = true;
    fig.classList.remove('is-live');
    fig.classList.add('is-still');
    put(name === DEFAULT ? 'named' : 'end');
    draw(END);
  }

  function setName(n) {
    var next = (n || '').replace(/\s+/g, ' ').trim() || DEFAULT;
    if (next === name) return;
    name = next;
    if (still) showStill(); else draw(frame);
  }
  document.addEventListener('season:name', function (e) { setName(e.detail && e.detail.name); });

  /* ------------------------------------------------------------ the film */
  var videos = [], current = null, paused = false, visible = true, dead = false, nudges = 0;
  function pick() {
    var w = stage.getBoundingClientRect().width * Math.min(window.devicePixelRatio || 1, 2);
    return w > 1100 ? '1600' : '960';
  }
  /* AV1 (10-bit) where it decodes in hardware, or where H.264 cannot play at all */
  function codecs(width) {
    var probe = document.createElement('video');
    var av1 = 'video/mp4; codecs="av01.0.08M.10"', h264 = 'video/mp4; codecs="avc1.640028"';
    var hasAv1 = !!probe.canPlayType(av1), hasH264 = !!probe.canPlayType(h264);
    if (!hasAv1) return Promise.resolve(hasH264 ? ['h264'] : []);
    if (!hasH264) return Promise.resolve(['av1']);
    if (!navigator.mediaCapabilities) return Promise.resolve(['h264', 'av1']);
    return navigator.mediaCapabilities.decodingInfo({ type: 'file', video: { contentType: av1, width: +width, height: width * 4 / 5, bitrate: 4e6, framerate: FPS } })
      .then(function (i) { return i.smooth && i.powerEfficient ? ['av1', 'h264'] : ['h264', 'av1']; }, function () { return ['h264', 'av1']; });
  }
  function video(src, loop) {
    var v = document.createElement('video');
    v.className = 'season-video';
    v.muted = v.defaultMuted = true;
    v.playsInline = true;
    v.loop = loop;
    ['muted', 'playsinline', 'disablepictureinpicture', 'disableremoteplayback'].forEach(function (a) { v.setAttribute(a, ''); });
    v.setAttribute('aria-hidden', 'true');
    v.setAttribute('tabindex', '-1');
    v.preload = loop ? 'none' : 'auto';
    v.src = src;
    return v;
  }
  /* call back once per presented frame, with its 1-based film frame */
  function onFrames(v, first, cb) {
    if (rvfc) {
      var tick = function (now, meta) {
        if (dead || !v.isConnected) return;
        v.requestVideoFrameCallback(tick);
        cb(first + Math.round(meta.mediaTime * FPS));
      };
      v.requestVideoFrameCallback(tick);
      return;
    }
    var last = -1;
    var poll = function () {
      if (dead || !v.isConnected) return;
      if (v.readyState >= 2 && v.currentTime !== last) { last = v.currentTime; cb(first + Math.round(v.currentTime * FPS)); }
      requestAnimationFrame(poll);
    };
    v.addEventListener('playing', function () { requestAnimationFrame(poll); }, { once: true });
  }
  function stop() {
    if (dead) return;
    dead = true;
    videos.forEach(function (v) { v.pause(); v.removeAttribute('src'); v.load(); v.remove(); });
    videos = []; current = null;
    if (pauseBtn) pauseBtn.hidden = true;
    showStill();
  }
  /* A pause() landing on a play() not yet started rejects it, and a codec the
     browser cannot decode rejects it too (the video's error event tries the
     other); only a refusal (autoplay blocked, a phone in low power) is the still */
  function play(v) {
    if (paused || !visible || document.hidden) return v.pause();
    var p = v.play();
    if (p && p.catch) p.catch(function (e) { if (!e || (e.name !== 'AbortError' && e.name !== 'NotSupportedError')) stop(); });
  }

  /* A film that has the next second in hand and still does not move is
     paused and played again; four tries that move nothing give up to the
     finished picture. WebKit's GStreamer build (Playwright's, on Linux) left
     the intro stuck on its second frame in about half of loads once the
     loop's pipeline started beside it: a seek did not free it, a pause and
     play did every time (30 Sep). A film waiting on the network has nothing
     buffered ahead and is left to wait. */
  var watched = -1, still4 = 0, tries = 0, timer = 0;
  function watch() {
    if (dead) return clearInterval(timer);
    var v = current;
    if (!v || paused || !visible || document.hidden || v.paused || v.ended) { still4 = 0; return; }
    var t = v.currentTime, b = v.buffered, ahead = 0;
    for (var i = 0; i < b.length; i++) if (b.start(i) <= t + 0.05 && b.end(i) > t) ahead = b.end(i) - t;
    if (t > watched + 0.1 || t < watched) { watched = t; still4 = 0; tries = 0; return; }
    if (v.readyState < 2 || !isFinite(v.duration) || (ahead < 1 && ahead < v.duration - t - 0.05)) { still4 = 0; return; }
    if (++still4 < 3) return;
    still4 = 0;
    if (++tries > 4) return stop();
    nudges++;
    v.pause();
    play(v);
  }

  /* The intro starts straight away, unseen: the first-frame poster is on the
     stage, and the intro shows itself on its own first frame, which is that
     picture. If the page gave up waiting first (is-late, six seconds), the
     finished picture is already up and the film does not start over it. */
  function run(order, width) {
    var codec = order[0];
    if (!codec) return stop();
    var intro = video(D.files.intro[width][codec], false);
    var loop = video(D.files.loop[width][codec], true);
    videos = [loop, intro];
    current = intro;
    stage.insertBefore(loop, panel);
    stage.insertBefore(intro, panel);
    var shown = false;
    onFrames(intro, 1, function (f) {
      if (!shown) {
        if (fig.classList.contains('is-late')) return stop();
        shown = true;
        fig.classList.add('is-running');
        intro.classList.add('is-on');
        loop.preload = 'auto';
        loop.load();
      }
      draw(f);
    });
    intro.addEventListener('ended', function () {
      var handed = false;
      /* the loop's own rows when the camera drifts in it (film.json keeps every
         frame then); a film still in its loop keeps one row, held */
      onFrames(loop, D.intro + 1, function (f) {
        draw(f);
        if (handed) return;
        handed = true;
        loop.classList.add('is-on');
        requestAnimationFrame(function () { intro.remove(); videos = [loop]; });
      });
      current = loop;
      play(loop);
    });
    intro.addEventListener('error', function () {
      if (dead) return;
      if (shown) return stop();
      /* this codec will not play here: the other one, then the still */
      intro.remove(); loop.remove();
      run(order.slice(1), width);
    }, { once: true });
    loop.addEventListener('error', stop, { once: true });
    play(intro);
    if (!timer) timer = setInterval(watch, 250);
  }

  /* ------------------------------------------------------------ pause (WCAG 2.2.2) */
  var pauseBtn = document.getElementById('season-pause');
  var pauseLabel = pauseBtn && pauseBtn.querySelector('.sr-only');
  function setPaused(p) {
    paused = p;
    pauseBtn.setAttribute('aria-pressed', String(p));
    pauseLabel.textContent = p ? 'Play the film' : 'Pause the film';
    fig.classList.toggle('is-paused', p);
    if (!current) return;
    play(current);
  }
  function wake() {
    if (!current || dead) return;
    play(current);
  }

  /* ------------------------------------------------------------ go */
  var fonts = document.fonts && document.fonts.load
    ? Promise.race([document.fonts.load('italic 400 ' + SIZE + 'px Fraunces'), new Promise(function (r) { setTimeout(r, 2500); })])
    : Promise.resolve();
  var who = document.getElementById('who');
  if (who && who.value.trim()) name = who.value.replace(/\s+/g, ' ').trim().slice(0, 24);
  fonts.then(function () {
    painted = '';
    if (window.ResizeObserver) new ResizeObserver(function () { drawn = ''; draw(frame); }).observe(stage);
    if (still) return showStill();
    if (pauseBtn) {
      pauseBtn.hidden = false;
      pauseBtn.addEventListener('click', function () { setPaused(!paused); });
    }
    if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { visible = es[0].isIntersecting; wake(); }).observe(fig);
    document.addEventListener('visibilitychange', wake);
    var width = pick();
    return codecs(width).then(function (order) { run(order, width); });
  }).catch(stop);

  /* for the hero check: what the window is doing */
  window.__heroFilm = function () {
    var poster = stage.querySelector('.season-poster');
    return { still: still, frame: frame, name: name, playing: !!current && !current.paused,
      part: current ? (current.loop ? 'loop' : 'intro') : null, src: current ? current.currentSrc : null,
      poster: poster && poster.getAttribute('data-poster'), written: panel.classList.contains('is-on'), fitted: fitted, nudges: nudges };
  };
})();
